import React, {useEffect, useMemo, useRef, useState} from 'react';
import {createRefetchContainer, graphql, RelayRefetchProp} from 'react-relay';
import {Breadcrumbs, FlexColumn, Heading, PinnableSummaryCards, TopNavigation} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import CustomScrollDiv from '../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import Styled from 'styled-components';
import Util from '../../forecast-app/shared/util/util';
import {isQuotesEnabled} from '../../forecast-app/shared/util/FeatureUtil';
import {LOCKED_QUOTE_STATUSES} from './quote-activity/quoteActivityCopy';
import {OnSaveError, OnSaveErrorType} from './Util';
import type {BaselineEditPage_viewer$data as BaselineEditPage_viewer} from './__generated__/BaselineEditPage_viewer.graphql';
import ProjectIndicator from '../../forecast-app/shared/components/project-indicator/ProjectIndicator';
import ActionBar from '../../forecast-app/navigation/header/ActionBar';
import {Redirect, useHistory} from 'react-router-dom';
import {projectUrl} from '../../directApi';
import {showModal, MODAL_TYPE} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {PhaseCard} from './PhaseDeliverablesCard';
import BulkUpdatePhaseBaselineRolesMutation from '../../mutations/bulk_update_phase_baseline_roles_mutation';
import BulkUpdatePhaseBaselineExpensesMutation from '../../mutations/bulk_update_phase_baseline_expenses_mutation';
import BulkUpdatePhaseDeliverablesMutation from '../../mutations/bulk_update_phase_deliverables_mutation';
import {
	formatBaselineEstimateTotal,
	getInitialBaselineItems,
	getInitialPhaseDeliverables,
	getMinutesPerDay,
	getTotals,
} from './Util';
import {
	BaselineItem,
	ExpenseRow,
	PhaseBaselineExpenseRequest,
	PhaseBaselineRoleRequest,
	ProjectPhaseDeliverable,
	RoleRow,
} from './BaselineTypes';
import ProjectUtil from '@/forecast-app/shared/util/ProjectUtil';

const PageWrapper = Styled.div`
  padding: 32px;
`;
const BreadcrumbsWrapper = Styled.div`
  padding-bottom: 24px;	
`;

interface BaselineEditPageProps {
	viewer: BaselineEditPage_viewer;
	relay: RelayRefetchProp;
}

const BaselineEditPage = ({viewer}: BaselineEditPageProps) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const history = useHistory();
	const scrollRef = useRef(null);
	const [onSaveErrors, setOnSaveErrors] = useState<OnSaveError[] | undefined>();

	const {company, project} = viewer;

	const useDeliverables = ProjectUtil.deliverablesUiEnabled(project);

	useEffect(() => {
		document.title = 'Baseline - Edit';
	}, []);

	const currency = (project?.rateCard ? project?.rateCard.currency : company?.currency) || '';

	const currencySymbol = Util.GetCurrencySymbol(currency);

	const [rolesUpdateSucceded, setRolesUpdateSucceded] = useState(false);
	const [expensesUpdateSucceded, setExpensesUpdateSucceded] = useState(false);

	if (rolesUpdateSucceded && expensesUpdateSucceded) {
		sessionStorage.setItem('baselineProjectUpdate', 'true');
		history.push(project ? `${projectUrl(project.companyProjectId, project.customProjectId)}/baseline` : ``);
	}

	const initialBaselineItems = useMemo(() => getInitialBaselineItems(project, useDeliverables), [project]);

	const initialPhaseDeliverables = useMemo(() => (useDeliverables ? getInitialPhaseDeliverables(project) : []), [project]);

	const [projectBaselineItems, setProjectBaselineItemsBaselineItems] = useState<BaselineItem[]>(initialBaselineItems);

	const [projectPhaseDeliverables, setProjectPhaseDeliverables] =
		useState<ProjectPhaseDeliverable[]>(initialPhaseDeliverables);

	const projectBaselineTotals = useMemo(() => {
		return getTotals(projectBaselineItems);
	}, [projectBaselineItems]);

	const sortedPhases = useMemo(() => {
		const phaseEdges = project?.phases?.edges || [];
		return ProjectUtil.sortPhasesBySortOrder(phaseEdges);
	}, [project?.phases?.edges]);

	const baselineRoles = projectBaselineItems?.filter(
		(item: BaselineItem): item is RoleRow => 'phaseBaselineRoleId' in item && item.roleId !== undefined
	) as RoleRow[];

	const baselineExpenses = projectBaselineItems?.filter(
		(item: BaselineItem): item is ExpenseRow => 'phaseBaselineExpenseId' in item && item.categoryId !== undefined
	) as ExpenseRow[];

	const rolesTotals = useMemo(() => getTotals(baselineRoles), [projectBaselineItems]);
	const expensesTotals = useMemo(() => getTotals(baselineExpenses), [projectBaselineItems]);

	const groupedProjectTotals = {rolesTotals, expensesTotals};

	const {baselineCost, baselineMargin, baselineMinutes, baselineProfit, baselineRevenue, baselineTimeAndExpenses} =
		projectBaselineTotals || {
			baselineCost: 0,
			baselineMargin: 0,
			baselineMinutes: 0,
			baselineProfit: 0,
			baselineRevenue: 0,
			baselineTimeAndExpenses: 0,
		};

	const estimateTotal = formatBaselineEstimateTotal(baselineMinutes || 0, project ? getMinutesPerDay(project) : null, intl);

	const validatePhaseBaselineRoles = (phaseBaselineRoles: PhaseBaselineRoleRequest[]) => {
		const errors: OnSaveError[] = [];

		phaseBaselineRoles.forEach(phaseBaselineRole => {
			if (!phaseBaselineRole.roleId) {
				errors.push({
					type: OnSaveErrorType.MISSING_ROLE,
					phaseId: phaseBaselineRole.phaseId,
					message: formatMessage({id: 'baseline.phase_deliverables_card.missing_role_error'}),
				});
			}
		});

		return errors;
	};

	const onClickSave = () => {
		setOnSaveErrors(undefined);

		const onPhaseDeliverablesUpdateSuccess = () => {
			const phaseBaselineRoles: PhaseBaselineRoleRequest[] = baselineRoles.map(item => {
				return {
					id: item.phaseBaselineRoleId,
					projectId: item.projectId,
					roleId: item.roleId,
					phaseId: item.phaseId,
					deliverableId: item.deliverableId,
					baselineMinutes: Math.round(item.hours * 60),
				};
			});

			const errors = validatePhaseBaselineRoles(phaseBaselineRoles);
			if (errors.length > 0) {
				setOnSaveErrors(errors);
				return;
			}

			Util.CommitMutation(BulkUpdatePhaseBaselineRolesMutation, {phaseBaselineRoles, projectId: project?.id}, () =>
				setRolesUpdateSucceded(true)
			);

			const phaseBaselineExpenses: PhaseBaselineExpenseRequest[] = baselineExpenses.map(item => {
				return {
					id: item.phaseBaselineExpenseId,
					projectId: item.projectId,
					expenseCategoryId: item.categoryId,
					phaseId: item.phaseId,
					deliverableId: item.deliverableId,
					expenseCost: item.cost,
					expenseMarkup: item.markup,
					expenseRevenue: item.revenue,
					expenseName: item.name,
				};
			});

			Util.CommitMutation(BulkUpdatePhaseBaselineExpensesMutation, {phaseBaselineExpenses, projectId: project?.id}, () =>
				setExpensesUpdateSucceded(true)
			);
		};

		const phaseDeliverablesMutation = {
			projectId: project?.id,
			phaseDeliverables: projectPhaseDeliverables.map(phaseDeliverable => ({
				id: phaseDeliverable.id,
				phaseId: phaseDeliverable.phaseId,
				deliverableId: phaseDeliverable.deliverable?.id,
				projectId: project?.id,
			})),
		};

		Util.CommitMutation(BulkUpdatePhaseDeliverablesMutation, phaseDeliverablesMutation, onPhaseDeliverablesUpdateSuccess);
	};

	const onClickCancel = () => {
		showModal({
			type: MODAL_TYPE.EXIT_ACTION_BAR_MODAL,
			callback: () => {
				history.push(project ? `${projectUrl(project.companyProjectId, project.customProjectId)}/baseline` : ``);
			},
		});
	};

	// A live quote locks the baseline: the edit route answers with a redirect, so a deep link or
	// stale tab cannot reach the bulk editors while locked. Reads the shared constant rather than
	// repeating it — this ticket is what makes the two able to disagree. Checked after every hook
	// has run so the hook order stays stable across renders.
	const quoteStatus = project?.quote?.status;
	const baselineLocked = isQuotesEnabled() && !!quoteStatus && LOCKED_QUOTE_STATUSES.includes(quoteStatus);
	if (baselineLocked && project) {
		return <Redirect to={`${projectUrl(project.companyProjectId, project.customProjectId)}/baseline`} />;
	}

	return (
		<>
			<TopNavigation>
				<ActionBar onClickSave={onClickSave} onClickCancel={onClickCancel}></ActionBar>
			</TopNavigation>
			<CustomScrollDiv
				setScrollbarsRef={node => {
					scrollRef.current = node;
				}}
			>
				<PageWrapper>
					<BreadcrumbsWrapper>
						<Breadcrumbs>
							<Breadcrumbs.Item>
								<FormattedMessage id="common.all_projects" />
							</Breadcrumbs.Item>
							{project && (
								<Breadcrumbs.Item>
									<div>
										{project.name}
										<ProjectIndicator project={project} />
									</div>
								</Breadcrumbs.Item>
							)}
							<Breadcrumbs.Item>
								<FormattedMessage id="baseline.edit_baseline.title" />
							</Breadcrumbs.Item>
						</Breadcrumbs>
					</BreadcrumbsWrapper>

					<FlexColumn gap={'l'}>
						<Heading size={'3'}>
							<FormattedMessage id="baseline.edit_baseline.title" />
						</Heading>

						<PinnableSummaryCards
							topOffset={57}
							summaryData={[
								{
									title: formatMessage({id: estimateTotal.titleId}),
									value: estimateTotal.value,
								},
								{
									title: formatMessage({id: 'common.total_value_of_service'}),
									value: Util.getFormattedNumberWithCurrency(
										currencySymbol,
										baselineTimeAndExpenses || 0,
										intl
									),
								},
								{
									title: formatMessage({id: 'common.total_revenue_capitalized'}),
									value: Util.getFormattedNumberWithCurrency(currencySymbol, baselineRevenue || 0, intl),
								},
								{
									title: formatMessage({id: 'common.total_cost'}),
									value: Util.getFormattedNumberWithCurrency(currencySymbol, baselineCost || 0, intl),
								},
								{
									title: formatMessage({id: 'common.total_profit'}),
									value: Util.getFormattedNumberWithCurrency(currencySymbol, baselineProfit || 0, intl),
								},
								{
									title: formatMessage({id: 'common.total_margin'}),
									value: Util.getFormattedPercentage(baselineMargin || 0, intl),
								},
							]}
						/>
					</FlexColumn>
					<FlexColumn gap="s">
						{!!project &&
							sortedPhases
								?.map(phaseEdge => {
									if (phaseEdge?.node && company) {
										const phase = phaseEdge.node;
										const items = projectBaselineItems
											.filter(item => item.phaseId === phase.id)
											.map((item, index) => ({...item, index}));
										return (
											<PhaseCard
												phase={phase}
												company={company}
												currency={currency}
												editMode={true}
												setProjectBaselineItems={setProjectBaselineItemsBaselineItems}
												setProjectPhaseDeliverables={setProjectPhaseDeliverables}
												phaseBaselineItems={items}
												projectPhaseDeliverables={projectPhaseDeliverables}
												groupedProjectTotals={groupedProjectTotals}
												project={project}
												useDeliverables={useDeliverables}
												onSaveErrors={onSaveErrors}
											/>
										);
									}
									return null;
								})
								.filter(Boolean)}
					</FlexColumn>
				</PageWrapper>
			</CustomScrollDiv>
		</>
	);
};

export default createRefetchContainer(
	BaselineEditPage,
	{
		viewer: graphql`
			fragment BaselineEditPage_viewer on Viewer
			@argumentDefinitions(projectId: {type: "String"}, hasDeliverablesFeature: {type: "Boolean!"}) {
				id
				company {
					id
					name
					currency
					roles {
						edges {
							node {
								id
								name
							}
						}
					}
					expenseCategories {
						edges {
							node {
								id
								name
								disabled
							}
						}
					}
				}
				project(id: $projectId) {
					...ProjectIndicator_project
					useBaseline
					synchBaselineAndScopingDates
					useDeliverables
					budgetType
					budget
					id
					status
					companyProjectId
					customProjectId
					quote {
						id
						status
					}
					projectColor
					name
					baselineEstimationMinutesPerDay
					projectStartYear
					projectStartMonth
					projectStartDay
					projectEndYear
					projectEndMonth
					projectEndDay
					baselineTargetMinutes
					baselineTarget
					baselineCost
					baselineProfit
					baselineTargetPrice
					useFixedPriceForBaselineRevenue
					rateCard {
						id
						currency
						disabledRoles {
							id
						}
						rates {
							edges {
								node {
									rate
									defaultRate
									role {
										id
									}
								}
							}
						}
					}
					financialNumbers(convertToProjectCurrency: true) {
						baselineMinutes
						baselineRevenue
						baselineTimeAndExpenses
						baselineCost
						baselineProfit
						baselineMargin
					}
					deliverables(first: 1000000)
						@connection(key: "Project_deliverables", filters: [])
						@include(if: $hasDeliverablesFeature) {
						edges {
							node {
								id
								name
								description
								projectId
								price
								invoicedAmount
								lockedRevenue
								hasBeenInvoiced
								hasLockedRevenue
							}
						}
					}
					phases(first: 1000000) @connection(key: "Project_phases", filters: []) {
						edges {
							node {
								id
								name
								sortOrder
								startYear
								startMonth
								startDay
								deadlineDay
								deadlineMonth
								deadlineYear
								baselineStartYear
								baselineStartMonth
								baselineStartDay
								baselineDeadlineYear
								baselineDeadlineMonth
								baselineDeadlineDay
								projectId
								portalConfig {
									requiresClientSignoff
									signoffApproverForecastPersonId
									signoffLocked
								}
								averageRatesByRole {
									roleId
									costRate
									rate
								}
								phaseDeliverables(first: 10000)
									@connection(key: "Phase_phaseDeliverables", filters: [])
									@include(if: $hasDeliverablesFeature) {
									edges {
										node {
											id
											phaseId
											deliverable {
												id
												name
											}
											financialNumbers(convertToProjectCurrency: true) {
												baselineMinutes
												baselineTimeAndExpenses
												baselineRevenue
												baselineCost
												baselineProfit
												baselineMargin
											}
										}
									}
								}
							}
						}
					}
					phaseBaselineExpenses(first: 1000000) @connection(key: "Project_phaseBaselineExpenses", filters: []) {
						edges {
							node {
								id
								expenseName
								expenseCategory {
									name
									id
								}
								expenseRevenue
								expenseCost
								expenseMarkup
								expenseProfit
								globalPhaseId
								deliverableId
							}
						}
					}
					phaseBaselineRoles(first: 1000000) @connection(key: "Project_phaseBaselineRoles", filters: []) {
						edges {
							node {
								id
								globalPhaseId
								deliverableId
								role {
									name
									id
								}
								baselineMinutes
								financialNumbers(convertToProjectCurrency: true) {
									baselineTimeAndExpenses
									baselineRevenue
									baselineCost
									baselineProfit
									baselineRatePerHour
									baselineCostPerHour
								}
							}
						}
					}
				}
			}
		`,
	},
	graphql`
		query BaselineEditPageRefetchQuery($projectId: String, $hasDeliverablesFeature: Boolean!) {
			viewer {
				component(name: "edit_baseline")
				...BaselineEditPage_viewer @arguments(projectId: $projectId, hasDeliverablesFeature: $hasDeliverablesFeature)
			}
		}
	`
);
